import { computed, type MaybeRefOrGetter, toValue } from "vue";

export interface FaqSchemaItem {
    question: string;
    answer: string;
}

export interface FaqPageQuestionSchema {
    "@type": "Question";
    name: string;
    acceptedAnswer: {
        "@type": "Answer";
        text: string;
    };
}

export interface FaqPageSchema {
    "@context": "https://schema.org";
    "@type": "FAQPage";
    mainEntity: FaqPageQuestionSchema[];
}

const ALLOWED_ANSWER_TAGS = /^(a|b|strong|i|em|br|ul|ol|li|p)$/i;

function sanitizeFaqAnswerHtml(value: string): string {
    return value
        .replace(/&nbsp;/gi, " ")
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
        .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, "")
        .replace(/<\/?([a-z][a-z0-9]*)\b[^>]*>/gi, (tag, tagName: string) => {
            return ALLOWED_ANSWER_TAGS.test(tagName) ? tag : "";
        })
        .replace(/\s+/g, " ")
        .trim();
}

export function buildFaqPageSchema(faqs: FaqSchemaItem[]): FaqPageSchema | null {
    const mainEntity = faqs
        .filter((faq) => faq.question?.trim() && faq.answer?.trim())
        .map((faq) => ({
            "@type": "Question" as const,
            name: faq.question.trim(),
            acceptedAnswer: {
                "@type": "Answer" as const,
                text: sanitizeFaqAnswerHtml(faq.answer),
            },
        }));

    if (mainEntity.length === 0) {
        return null;
    }

    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity,
    };
}

export function useFaqPageSchema(faqs: MaybeRefOrGetter<FaqSchemaItem[]>) {
    const schema = computed(() => buildFaqPageSchema(toValue(faqs)));
    const schemaJson = computed(() => (schema.value ? JSON.stringify(schema.value) : null));

    return {
        schema,
        schemaJson,
    };
}
